// src/view/camera.ts — Arena kamerası (DESIGN §10, ARCHITECTURE §7): sabit yaw 0, pitch 26°, FOV 40; odak kutusu (aktif kapsüller ∪
// merkez, pay 1.5) → 8 köşe / tanH-tanV mesafesi ×1.1, clamp [10, 28]; asimetrik damp (uzak 8 / yakın 3 / hedef 6); y < −4 olan 21 t
// daha izlenir; sarsıntı yumuşatmadan SONRA (Perlin-benzeri smoothNoise, 2 eksen + roll), birikmez (max), ×clamp(dist/18, 1, 1.25);
// YEREL OYUNCU ilgisi kademesi (hafif/ağır yalnız olay yerel oyunculardan BİRİNİ ilgilendiriyorsa; 3. şahıs ağır ×0.3,
// knockdown/parry ×0.5 — genlik VE süre, en fazla 4/s; yerel oyuncu yoksa hiçbir olay "ilgilendiren" değil — duman testi);
// ring-out zoom kick; VURUŞ AĞIR ÇEKİMİ YAKINLAŞMASI (§10, 4. tur): pencere boyunca kadraj vuran+yiyen ikilisinin göğüs
// hizasına kayar ve mesafe ×0.60'a (taban 6.5 u) iner — yumuşatmadan SONRA, `viewTarget`/`viewDist`e; temel `target`/`dist`
// dokunulmaz, karışım ağırlığı smoothstep'lidir → çıkışta pop yok. Hitstop'ta donmaz (dt render). Odak: Spawn_Air'de model
// havada (hips Y klipte) → kutuya spawn yüksekliği eklenir; düşen dövüşçü (Falling hariç) 0.3 s'de kenar noktasına söner → arena küçülmez.
import * as THREE from 'three';
import type { ArenaCameraApi, ArenaDef, CameraShake, Fighter, SimEvent, SimWorld, Vec3, SlowMoState } from '../core/types.ts';
import { Ev, FState, HitKind } from '../core/types.ts';
import { clamp, clamp01, degToRad, expDecay, lerp, smoothNoise, smoothstep } from '../core/math.ts';
import { CAP_H, CAP_R, MAX_FIGHTERS, NO_TICK } from '../config/constants.ts';
import { T } from '../config/tuning.ts';
import { interpPos } from './fighterView.ts';
import { walkableBounds } from './arenaView.ts';
import { hitSlowMoProgress } from './slowMo.ts';

/** Sarsıntı gürültü frekansı (düğüm/s; DESIGN "Perlin 2 eksen", sayı yok → 24 Hz yerel, needsWP0). */
const SHAKE_FREQ = 24;
/** 3. şahıs knockdown / throw / parry / finisher sarsıntı çarpanı (orkestratör kararı 1; DESIGN §10 "herkes" → kalabalıkta sürekli
 *  titreme). Sayı tuning CAMERA.shakeOtherKnockdownMult (entegrasyon eki); burada yalnız takma ad. Ring-out herkes için ×1 kalır. */
export const SHAKE_OTHER_KNOCKDOWN_MULT = T.CAMERA.shakeOtherKnockdownMult;
/** 3. şahıs (mult < 1) sarsıntıları için en küçük aralık (s): shake() "birikmez, max" kuralıyla her yeni olayda süreyi baştan
 *  başlatır → 8 dövüşçüde sık knockdown/parry olaylarında kamera zamanın ~%30'unda sarsılıyordu (all-bot smoke). Oyuncuyu ilgilendiren
 *  ve ring-out sarsıntıları aralıktan muaf. Sayı 3. turda tuning'e taşındı (T.CAMERA.shakeOtherCooldown); burada yalnız takma ad. */
const SHAKE_OTHER_COOLDOWN: number = T.CAMERA.shakeOtherCooldown;
/** Düşen dövüşçünün odak kutusuna KATKI AĞIRLIĞI 1 → 0 (t): platform üst yüzeyinin altına indiği tick'ten sonra. Kutu düşen bedeni
 *  kill plane'e (−8) kadar tam takip edince 8-köşe mesafesi ~10'dan ~20'ye çıkıyor, arena ekranın ~%40'ına iniyordu (3. tur bulgusu:
 *  her ring-out'ta ~1 s okunmaz kadraj). Ağırlık 0'a inince dövüşçünün katkısı en yakın ARENA KENAR noktasına eşitlenir → kadraj
 *  arenayı bırakmaz. DESIGN §10 izleme penceresi (y < −4 olan 21 t daha; inFocus) DEĞİŞMEZ, yalnız katkı yumuşak söner — pencerenin
 *  sonundaki sert "kadraj sıçraması" da kalkar: sönüm üst yüzeyin altında BAŞLADIĞI için (izleme penceresi −4'te başlar ve
 *  fallWatchTicks 21 t sürer) ağırlık, pencere kapanmadan önce mutlaka 0'a iner — FALL_FADE_TICKS < EDGE.fallWatchTicks şartı.
 *  Sayı 3. turda tuning'e taşındı (T.CAMERA.fallFocusFadeTicks; şart orada da yazılı). */
const FALL_FADE_TICKS: number = T.CAMERA.fallFocusFadeTicks;   // 0.3 s
/** Spawn_Air'de hips'in klipteki başlangıç yüksekliği (ANIMATION §2 tablo: 2.0 → 0.28; fizik kapsülü yerde). Odak kutusu bunu
 *  görmezse geri sayımda model kadrajın üstünden girer. Sayı ANIMATION ölçümü; tuning'de yok → yerel (needsWP0). */
const SPAWN_AIR_HIPS_Y = 2.0;
/** Düşüş takibinde odak kutusunun arena üst yüzeyinin altına inebileceği en fazla derinlik (u; yerel, needsWP0): −4'ün altı 21 t daha
 *  izlenir, bu sırada kutu tabanı sınırsız aşağı kaymasın (kamera dalıp arena kadrajdan çıkmasın). */
const FALL_FOCUS_DEPTH = 8;

/** 8 köşe / tanH-tanV mesafesi (viewer Stage.frame): kutu merkezden, dir/right/up birim. */
export function frameDistance(min: Vec3, max: Vec3, center: Vec3, dir: Vec3, right: Vec3, up: Vec3, tanV: number, tanH: number): number {
  let dist = 0;
  for (let i = 0; i < 8; i++) {
    const cx = (i & 1 ? max.x : min.x) - center.x, cy = (i & 2 ? max.y : min.y) - center.y, cz = (i & 4 ? max.z : min.z) - center.z;
    const z = cx * dir.x + cy * dir.y + cz * dir.z;
    const r = Math.abs(cx * right.x + cy * right.y + cz * right.z), u = Math.abs(cx * up.x + cy * up.y + cz * up.z);
    dist = Math.max(dist, r / tanH + z, u / tanV + z);
  }
  return dist;
}

/** Bir dövüşçü kimliği YEREL insan oyunculardan birine mi ait (aynı ekranda 1–4 kişi; config/players MAX_LOCAL_PLAYERS).
 *  Kamera "oyuncu" kavramını tek id yerine bu testle taşır: tek oyuncuda `isLocalOf(playerId)`, çok oyuncuda id listesi. */
export type IsLocal = (id: number) => boolean;
/** Yerel oyuncu yok (duman testi / izleyici): hiçbir olay "yerel oyuncuyu ilgilendiren" değil. */
export const NO_LOCAL: IsLocal = () => false;
/** Yerel oyuncu testi kurar. Kabul: hazır test, tek id (< 0 → kimse), id listesi (boş → kimse).
 *  Liste yolu bit maskesine indirgenir (dövüşçü kimlikleri < MAX_FIGHTERS = 8) → çağrı başına tahsis ve döngü yok;
 *  test her karede odak kutusunda dövüşçü başına bir kez çağrılıyor. */
export type LocalSpec = IsLocal | number | readonly number[];
export function isLocalOf(spec: LocalSpec): IsLocal {
  if (typeof spec === 'function') return spec;
  let mask = 0;
  if (typeof spec === 'number') { if (spec >= 0 && spec < MAX_FIGHTERS) mask = 1 << spec; }
  else for (const id of spec) if (id >= 0 && id < MAX_FIGHTERS) mask |= 1 << id;
  if (mask === 0) return NO_LOCAL;
  const m = mask;
  return (id) => id >= 0 && ((m >>> id) & 1) !== 0;
}

/** Ağır çekim ikilisinin odak noktası: ayak hizası + CAP_H × bu oran ≈ göğüs (kamera gözü karnına değil vuruşa baksın). */
const PAIR_EYE = 0.55;
/** λ = BLEND_95 / süre → verilen sürede karışımın %95'i biter (ln 20 ≈ 3.0). Süreler saniye cinsinden tuning'de duruyor. */
const BLEND_95 = 3;

/** Karışımın hedefe oturma eşiği: üssel kuyruk yüzünden b asla tam 0/1 olmaz, kamera sonsuza dek mikroskobik bir
 *  kaydırma taşırdı. Eşikte uygulanan sapma smoothstep(1e-3) ≈ 3e-6 → mesafede ~1e-4 u, görülemez. */
const BLEND_SNAP = 1e-3;

/**
 * VURUŞ AĞIR ÇEKİMİ KAMERASI (DESIGN §10, 4. tur; kullanıcı: "vuruşu detaylı izlemeliyim Party Animals gibi").
 * Karışım ağırlığı b: pencere içinde 1'e, dışında 0'a koşar. Giriş HIZLI (0.10 s), çıkış YAVAŞ (0.30 s) — asimetri
 * bilerek: hızlı çıkış kadrajı "pat" diye açıyor.
 */
export function stepHitSlowMoBlend(cur: number, target: number, dt: number): number {
  const sec = target > cur ? T.CAMERA.hitSlowMoBlendIn : T.CAMERA.hitSlowMoBlendOut;
  const next = expDecay(cur, target, BLEND_95 / Math.max(sec, 1e-3), dt);
  return Math.abs(next - target) < BLEND_SNAP ? target : next;
}

/**
 * Ham karışım b → UYGULANAN ağırlık w. Hermite (smoothstep) bilerek: w'(0) = w'(1) = 0, yani kamera hem pencereye
 * girerken hem çıkarken SIFIR hızla başlar. Ham üssel b tek başına kullanılınca pencere bittiği anda hız birden en
 * yüksek değerine fırlıyordu — kadraj geniş bir maçta bu "pat" diye geri açılma olarak görünüyor (istenmeyen pop).
 */
export const hitSlowMoWeight = (b: number): number => smoothstep(0, 1, b);

/**
 * Yakınlaşmış mesafe: temel (kadrajlanmış, clamp'li) mesafe × lerp(1, hitSlowMoZoom, b), TABAN da b ile iner
 * (minDist 10 → hitSlowMoMinDist 6.5). Ayrı taban şart: tipik dövüşte 8-köşe mesafesi zaten minDist'e kelepçeli
 * olduğu için çarpan tek başına hiçbir şey yapmıyordu. maxDist tavanı korunur — kadraj kuralları (8 köşe, kenar
 * payı) DEĞİŞMEZ, yakınlaşma yalnızca sonuca uygulanan bir çarpandır.
 * b = 0'da sonuç TAM `base`tir (base zaten clamp'li) → pencere bitince pop olmaz, bu fonksiyon kimliğe döner.
 */
export function hitSlowMoDist(base: number, b: number, pairSpan = 0): number {
  const w = hitSlowMoWeight(b);
  if (w <= 0) return base;   // pencere dışı: kimlik (pop yok)
  // İKİLİYİ ÇERÇEVELE (mutlak), tabana ORAN uygulama. Çarpan yaklaşımı kalabalık maçta çalışmıyordu: 4 dövüşçüde
  // taban mesafe ~24 olduğu için ×0.60 bile 14.4 veriyor ve dövüşçü ekranın ancak %20'si kalıyordu (ölçüldü).
  // Artık hedef mesafe yalnız İKİLİNİN ARASINDAKİ AÇIKLIĞA bağlı: kaç kişi dövüşürse dövüşsün aynı yakın plan.
  const want = T.CAMERA.hitSlowMoMinDist + pairSpan * T.CAMERA.hitSlowMoSpanMult;
  // Taban zaten daha yakınsa uzaklaştırma (küçük arenada kamera GERİ gitmesin): alt sınır da tabanla kısıtlı.
  // (Testte yakalandı: sabit 6.5 alt sınırı, tabanı 5.5 olan bir kadrajda kamerayı geri itiyordu.)
  const lo = Math.min(T.CAMERA.hitSlowMoMinDist, base);
  return clamp(lerp(base, Math.min(want, base), w), lo, T.CAMERA.maxDist);
}

export interface ShakeChoice { shake: CameraShake; mult: number }
/** shakeForEvent'in çıktısı: olay yolunda tahsis olmasın diye modül scratch'i (çağıran hemen tüketir; saklamaz). */
const _choice: ShakeChoice = { shake: T.CAMERA.shakes.light, mult: 1 };
const pick = (shake: CameraShake, mult: number): ShakeChoice => { _choice.shake = shake; _choice.mult = mult; return _choice; };

/** Olay → sarsıntı (DESIGN §10 tablosu + orkestratör kararı 1). involves: actor/target YEREL oyunculardan biri (aynı ekranda 2–4 kişi
 *  varsa herhangi biri yeter — ekran ortak). 3. şahıs: hafif 0, ağır ×0.3, knockdown / throw / parry / finisher
 *  ×SHAKE_OTHER_KNOCKDOWN_MULT; ring-out herkes ×1. Dönen nesne paylaşılan scratch'tir. */
export function shakeForEvent(e: SimEvent, isLocal: IsLocal): ShakeChoice | null {
  // Yerel oyuncu yoksa (izleyici / ?smoke all-bot) NO_LOCAL her id için false döner: hedefsiz olaylar (Ev.Knockdown target = NO_ID)
  // eskiden −1 === −1 ile tam genlik + muafiyet alıyordu, kalabalıkta kamera sürekli titriyordu (3. tur: 8 dövüşçü shakeRatio 0.355).
  const involves = (e.actor >= 0 && isLocal(e.actor)) || (e.target >= 0 && isLocal(e.target));
  const S = T.CAMERA.shakes;
  const kdMult = involves ? 1 : SHAKE_OTHER_KNOCKDOWN_MULT;
  switch (e.type) {
    case Ev.Hit: {
      const kind = e.v2 as HitKind;
      if (kind === HitKind.Finisher || kind === HitKind.Tumble) return pick(S.knockdown, kdMult);
      if (kind === HitKind.Mid) return pick(S.heavy, involves ? 1 : T.CAMERA.shakeOtherHeavyMult);
      return involves ? pick(S.light, 1) : null;
    }
    case Ev.GuardBreak: case Ev.BodyImpact:
      return pick(S.heavy, involves ? 1 : T.CAMERA.shakeOtherHeavyMult);
    case Ev.Knockdown: case Ev.Throw: return pick(S.knockdown, kdMult);
    case Ev.Parry: return pick(S.parry, kdMult);
    case Ev.Fell: return pick(S.ringout, 1);
    default: return null;
  }
}

/** Kadraja girer mi (DESIGN §10 / ARCHITECTURE §7): Respawning/Eliminated hariç; y < −4 olan fallWatchTicks kadar daha.
 *  Falling da hariç: kill plane'i geçmiş beden ("kill plane −8 ayrı") artık aktif dövüşçü değil — 0.25 s'de ölçeği 0'a iner, HUD
 *  etiketi ve VFX'i de kapalı; kadrajı 1.5 s boyunca −8'e çekip arenayı küçültmesi okunabilirliği bozuyordu (3. tur bulgusu). */
export function inFocus(f: Fighter, belowSince: number, tick: number): boolean {
  if (f.state === FState.Respawning || f.state === FState.Eliminated || f.state === FState.Falling) return false;
  if (f.pos.y >= T.EDGE.fallWatchY) return true;
  return belowSince !== NO_TICK && tick - belowSince <= T.EDGE.fallWatchTicks;
}

const _p: Vec3 = { x: 0, y: 0, z: 0 };
const _min: Vec3 = { x: 0, y: 0, z: 0 };
const _max: Vec3 = { x: 0, y: 0, z: 0 };
const _center: Vec3 = { x: 0, y: 0, z: 0 };
const _dir: Vec3 = { x: 0, y: 0, z: 0 };
const _right: Vec3 = { x: 1, y: 0, z: 0 };
const _up: Vec3 = { x: 0, y: 0, z: 0 };
const _look = new THREE.Vector3();

export class ArenaCamera implements ArenaCameraApi {
  readonly yaw = 0;
  /** Yumuşatılmış hedef nokta ve mesafe. */
  readonly target = new THREE.Vector3();
  dist = T.CAMERA.minDist;
  /** Son hedefler (debug/test). */
  readonly goal = new THREE.Vector3();
  goalDist = T.CAMERA.minDist;
  /** SUNUM hedefi/mesafesi: `target`/`dist` üzerine vuruş ağır çekimi yakınlaşması uygulanmış hâli — kamera bunları
   *  kullanır. Yumuşatılmış temel durum (`target`/`dist`) ETKİLENMEZ; yakınlaşma bittiğinde kamera hiçbir yakalama
   *  yapmadan tam olması gereken yerde olur (pop yok). */
  readonly viewTarget = new THREE.Vector3();
  /** Ağır çekimdeki ikilinin yatay açıklığı (u) — yakın plan mesafesi buna göre. */
  private pairSpan = 0;
  viewDist = T.CAMERA.minDist;
  /** Yakınlaşma karışımı 0..1 (0 = normal kadraj, 1 = ikilinin üstünde). */
  hitBlend = 0;
  /** Ağır çekim ikilisinin göğüs hizasındaki ortası (dünya). Pencere kapandıktan sonra da güncellenir: çıkış
   *  karışımı boyunca kamera donuk bir noktaya değil, hareket eden ikiliye göre çözülür. */
  private readonly pair = new THREE.Vector3();
  private readonly belowSince = new Int32Array(MAX_FIGHTERS).fill(NO_TICK);
  /** Platform üst yüzeyinin altına indiği tick (düşüş ağırlığı sönümü; NO_TICK = yukarıda). */
  private readonly underSince = new Int32Array(MAX_FIGHTERS).fill(NO_TICK);
  private shakeAmp = 0; private shakeDur = 0; private shakeT = 0; private shakeRoll = 0;
  private lastOtherShakeTime = -Infinity;
  private kickFrac = 0; private kickDur = 0; private kickT = 0;
  private time = 0;
  /** Toplam sarsıntı süresi (?smoke kabulü: 20 s'de < %25). */
  shakeSeconds = 0;
  /** Yürünebilir arena sınırları (düşüş takibinde kenar noktası kutuda kalsın). */
  private readonly bounds: { minX: number; maxX: number; minZ: number; maxZ: number; topY: number };

  /** Yerel insan oyuncu testi (sarsıntı kademesi + düşüş odağı muafiyeti). Tek oyuncuda id, çok oyuncuda id listesi verilir;
   *  maç içinde değişebilir (oyuncu ayrılır/katılır) → setLocal. */
  private isLocal: IsLocal;

  constructor(readonly camera: THREE.PerspectiveCamera, readonly arena: ArenaDef, local: LocalSpec, readonly seed = 1) {
    this.isLocal = isLocalOf(local);
    this.bounds = walkableBounds(arena);
    const p = degToRad(T.CAMERA.pitchDeg);
    _dir.x = 0; _dir.y = Math.sin(p); _dir.z = Math.cos(p);          // kamera arenanın +Z tarafında, −Z'ye bakar (W = −Z = uzak kenar)
    _up.x = 0; _up.y = Math.cos(p); _up.z = -Math.sin(p);
    this.target.set(arena.center.x, arena.center.y, arena.center.z);
    this.goal.copy(this.target);
    this.viewTarget.copy(this.target);
    this.viewDist = this.dist;
    camera.fov = T.CAMERA.fovDeg;
    camera.updateProjectionMatrix();
    this.apply(0);
  }

  /** Yerel oyuncu kümesini değiştirir (dövüşçü kimlikleri belli olduğunda / bir oyuncu ayrıldığında). Kadraj DEĞİŞMEZ:
   *  odak kutusu her zaman TÜM dövüşçüleri çerçeveler; bu küme yalnız sarsıntı kademesini ve düşüş odağı muafiyetini etkiler. */
  setLocal(local: LocalSpec): void { this.isLocal = isLocalOf(local); }

  /** Olayları tüketir (game.ts presenter.consume içinden). */
  onEvent(e: SimEvent): void {
    const s = shakeForEvent(e, this.isLocal);
    if (s) this.shake(s.shake, s.mult);
    if (e.type === Ev.Fell) this.zoomKick(T.CAMERA.ringoutZoom, T.CAMERA.ringoutZoomDur);
  }
  /** Birikmez: kalan genlikten büyükse değiştirir. `mult` genliği (ve roll'u) ölçekler — tahsissiz 3. şahıs kademesi; 3. şahıs
   *  (mult < 1) sarsıntıları SHAKE_OTHER_COOLDOWN aralığından sık gelirse yok sayılır (kalabalıkta sürekli titreme olmasın)
   *  ve SÜRELERİ de mult ile ölçeklenir: yalnız genlik küçültülünce her 3. şahıs olayı tam süre (knockdown 0.22 s) sarsıntı
   *  başlatıyor, 8 dövüşçüde kamera zamanın üçte birinde titriyordu (3. tur bulgusu; orkestratör kararı 1'in devamı, needsWP0). */
  shake(s: CameraShake, mult = 1): void {
    const amp = s.amp * mult;
    const remaining = this.shakeDur > 0 ? this.shakeAmp * (1 - this.shakeT / this.shakeDur) : 0;
    if (amp <= remaining) return;
    if (mult < 1) {
      if (this.time - this.lastOtherShakeTime < SHAKE_OTHER_COOLDOWN) return;
      this.lastOtherShakeTime = this.time;
    }
    this.shakeAmp = amp; this.shakeDur = s.dur * (mult < 1 ? mult : 1); this.shakeT = 0; this.shakeRoll = (s.roll ?? 0) * mult;
  }
  zoomKick(frac: number, dur: number): void { this.kickFrac = frac; this.kickDur = dur; this.kickT = 0; }

  /** Odak kutusu → _min/_max (interpolasyonlu konumlar; kapsül ± CAP_R, 0..CAP_H; ∪ arena merkezi; pay).
   *  Spawning: model Spawn_Air'de hips 2.0 u yukarıda başlar → tepe SPAWN_AIR_HIPS_Y kadar yukarı (geri sayımda kadraj üstünden girmesin).
   *  Düşen (y < kenar üstü): en yakın arena kenar noktası (x/z sınırlara kelepçeli, topY) kutuya girer ve taban FALL_FOCUS_DEPTH'ten
   *  aşağı inmez → arena kenarı hep kadrajda (finding: düşüşte siyah kare). Ayrıca düşüşün ilk FALL_FADE_TICKS'inde katkı ağırlığı
   *  1 → 0 iner: nokta kendi konumundan bu kenar noktasına kayar, yani kadraj bedeni −8'e kadar takip edip arenayı küçültmez
   *  (3. tur bulgusu). YEREL OYUNCULAR muaf (ağırlık 1): kendi düşüşünde toparlama denemesi (çift zıplama / hava dodge) kadrajda
 *  kalmalı — aynı ekranda kaç kişi varsa hepsi için. */
  private focusBox(world: SimWorld, alpha: number): void {
    const c = this.arena.center;
    const b = this.bounds;
    _min.x = c.x; _min.y = c.y; _min.z = c.z; _max.x = c.x; _max.y = c.y; _max.z = c.z;
    for (const f of world.fighters) {
      const below = f.pos.y < T.EDGE.fallWatchY;
      if (below) { if (this.belowSince[f.id] === NO_TICK) this.belowSince[f.id] = world.tick; } else this.belowSince[f.id] = NO_TICK;
      const under = f.pos.y < b.topY;
      if (under) { if (this.underSince[f.id] === NO_TICK) this.underSince[f.id] = world.tick; } else this.underSince[f.id] = NO_TICK;
      if (!inFocus(f, this.belowSince[f.id] ?? NO_TICK, world.tick)) continue;
      interpPos(_p, f, alpha);
      // Düşüş ağırlığı: 1 = kendi konumu, 0 = en yakın arena kenar noktası (kelepçeli x/z, üst yüzey)
      const wgt = !under || this.isLocal(f.id) ? 1
        : 1 - clamp01((world.tick - (this.underSince[f.id] ?? world.tick)) / FALL_FADE_TICKS);
      const px = under ? lerp(clamp(_p.x, b.minX, b.maxX), _p.x, wgt) : _p.x;
      const pz = under ? lerp(clamp(_p.z, b.minZ, b.maxZ), _p.z, wgt) : _p.z;
      const py = under ? lerp(b.topY, Math.max(_p.y, b.topY - FALL_FOCUS_DEPTH), wgt) : _p.y;
      const top = py + CAP_H + (f.state === FState.Spawning ? SPAWN_AIR_HIPS_Y : 0);
      _min.x = Math.min(_min.x, px - CAP_R); _max.x = Math.max(_max.x, px + CAP_R);
      _min.y = Math.min(_min.y, py); _max.y = Math.max(_max.y, top);
      _min.z = Math.min(_min.z, pz - CAP_R); _max.z = Math.max(_max.z, pz + CAP_R);
      if (under) _max.y = Math.max(_max.y, b.topY);   // düşüşte arena üst yüzeyi hep kadrajda
    }
    const m = T.CAMERA.margin;
    _min.x -= m; _min.y -= m; _min.z -= m; _max.x += m; _max.y += m; _max.z += m;
  }

  /**
   * Ağır çekim ikilisinin (vuran + yiyen) göğüs hizasındaki ortası → `this.pair`. Vuran NO_ID olabilir (uçan eşya)
   * → o zaman yalnız kurban. false = ikili bulunamadı; çağıran son değeri korur. Tahsissiz (modül scratch _p).
   */
  private pairMid(world: SimWorld, alpha: number, slow: SlowMoState): boolean {
    const a = slow.actor >= 0 ? world.fighters[slow.actor] : undefined;
    const v = slow.victim >= 0 ? world.fighters[slow.victim] : undefined;
    let n = 0, x = 0, y = 0, z = 0;
    if (a) { interpPos(_p, a, alpha); x += _p.x; y += _p.y; z += _p.z; n++; }
    if (v) { interpPos(_p, v, alpha); x += _p.x; y += _p.y; z += _p.z; n++; }
    if (n === 0) return false;
    this.pair.set(x / n, y / n + CAP_H * PAIR_EYE, z / n);
    // İkilinin yatay açıklığı: yakın plan mesafesi buna göre seçilir (kalabalıktan bağımsız, tutarlı kadraj).
    if (a && v) {
      interpPos(_p, a, alpha); const ax = _p.x, az = _p.z;
      interpPos(_p, v, alpha);
      this.pairSpan = Math.hypot(_p.x - ax, _p.z - az);
    } else this.pairSpan = 0;
    return true;
  }

  /**
   * Vuruş ağır çekiminde kadrajı ikilinin üstüne kaydırır (DESIGN §10). Yumuşatmadan SONRA, sunum katmanında:
   * `target`/`dist` temel kadrajı taşımaya devam eder, yakınlaşma yalnız `viewTarget`/`viewDist`e yazılır.
   * ÇOK OYUNCU: dört kişilik maçta diğerleri kısa süre kadraj dışında kalabilir — kabul edilen bir bedel
   * (an ≈ 0.9 s), pencere bitince `hitBlend` 0'a inip herkes kadraja döner.
   */
  private updateHitZoom(world: SimWorld, alpha: number, dt: number, slow: SlowMoState): void {
    const inWin = hitSlowMoProgress(slow, world.tick) >= 0;
    let want = 0;
    if (inWin || this.hitBlend > 1e-4) { const ok = this.pairMid(world, alpha, slow); if (inWin && ok) want = 1; }
    this.hitBlend = stepHitSlowMoBlend(this.hitBlend, want, dt);
    const b = this.hitBlend;
    if (b <= 0) { this.viewTarget.copy(this.target); this.viewDist = this.dist; return; }
    const w = hitSlowMoWeight(b);   // hedef noktası ve mesafe AYNI ağırlığı kullanır (ikisi birlikte hareket etsin)
    this.viewTarget.set(lerp(this.target.x, this.pair.x, w), lerp(this.target.y, this.pair.y, w), lerp(this.target.z, this.pair.z, w));
    this.viewDist = hitSlowMoDist(this.dist, b, this.pairSpan);
  }

  update(world: SimWorld, alpha: number, dt: number, slow: SlowMoState): void {
    this.time += dt;
    this.focusBox(world, alpha);
    _center.x = (_min.x + _max.x) / 2; _center.y = (_min.y + _max.y) / 2; _center.z = (_min.z + _max.z) / 2;
    const tanV = Math.tan(degToRad(this.camera.fov) / 2), tanH = tanV * this.camera.aspect;
    let d = frameDistance(_min, _max, _center, _dir, _right, _up, tanV, tanH) * T.CAMERA.distMult;
    d = clamp(d, T.CAMERA.minDist, T.CAMERA.maxDist);
    this.goal.set(_center.x, _center.y, _center.z);
    this.goalDist = d;
    // Asimetrik yumuşatma (uzaklaşma hızlı, yakınlaşma sakin); hedef nokta λ 6
    const lam = d > this.dist ? T.CAMERA.lambdaOut : T.CAMERA.lambdaIn;
    this.dist = expDecay(this.dist, d, lam, dt);
    this.target.x = expDecay(this.target.x, _center.x, T.CAMERA.lambdaTarget, dt);
    this.target.y = expDecay(this.target.y, _center.y, T.CAMERA.lambdaTarget, dt);
    this.target.z = expDecay(this.target.z, _center.z, T.CAMERA.lambdaTarget, dt);
    this.updateHitZoom(world, alpha, dt, slow);   // yumuşatmadan SONRA: temel kadraj bozulmaz, yakınlaşma sunuma uygulanır
    // Zoom kick (ring-out +%8, 0.15 s'de sönen)
    let kick = 0;
    if (this.kickDur > 0) { this.kickT += dt; kick = this.kickT >= this.kickDur ? 0 : this.kickFrac * (1 - this.kickT / this.kickDur); if (kick === 0) this.kickDur = 0; }
    this.apply(kick);
    this.tickShake(dt);
  }

  /** Konum = SUNUM hedefi + dir·SUNUM mesafesi·(1+kick); sonra sarsıntı (right/up eksenleri) + roll.
   *  `viewTarget`/`viewDist` = temel kadraj + vuruş ağır çekimi yakınlaşması (yakınlaşma yokken ikisi aynı). */
  private apply(kick: number): void {
    const dist = this.viewDist * (1 + kick);
    const cam = this.camera;
    cam.position.set(this.viewTarget.x + _dir.x * dist, this.viewTarget.y + _dir.y * dist, this.viewTarget.z + _dir.z * dist);
    let roll = 0;
    if (this.shakeDur > 0) {
      const dt = this.time; // gürültü zamanı sürekli
      const k = 1 - this.shakeT / this.shakeDur;
      const scale = clamp(dist / T.CAMERA.shakeDistRef, 1, T.CAMERA.shakeDistScaleMax);
      const a = this.shakeAmp * k * scale;
      const nx = smoothNoise(this.seed, dt * SHAKE_FREQ, 1), ny = smoothNoise(this.seed, dt * SHAKE_FREQ, 2);
      cam.position.x += _right.x * nx * a + _up.x * ny * a;
      cam.position.y += _right.y * nx * a + _up.y * ny * a;
      cam.position.z += _right.z * nx * a + _up.z * ny * a;
      roll = degToRad(this.shakeRoll) * k * smoothNoise(this.seed, dt * SHAKE_FREQ, 3);
    }
    _look.copy(this.viewTarget);
    cam.lookAt(_look);
    if (roll !== 0) cam.rotateZ(roll);
  }

  /** Sarsıntı sayaçlarını ilerletir (update sonunda çağrılır; ayrı tutuldu ki apply(0) kurulumda güvenli). */
  tickShake(dt: number): void {
    if (this.shakeDur > 0) { this.shakeT += dt; this.shakeSeconds += dt; if (this.shakeT >= this.shakeDur) this.shakeDur = 0; }
  }
  get shaking(): boolean { return this.shakeDur > 0; }
}
